import React, { useState, useRef, useEffect } from "react";

function getHostEl() {
  // Try search widget host first (for embedded calendar in search widget)
  const searchHost = (
    document.querySelector("#nexus-search-widget") ||
    document.querySelector(".nexus-search-widget") ||
    document.querySelector("[data-nexus-search]")
  );

  if (searchHost) return searchHost;

  // Fallback: look for calendar container (for standalone calendar or preview)
  return (
    document.querySelector("[data-nexus-calendar]") ||
    document.querySelector(".nexus-calendar-section-dropdown") ||
    document.querySelector("#nexus-calendar-section") ||
    document.querySelector(".nexus-calendar")
  );
}

function readCalendarIconSpec() {
  const el = getHostEl();
  if (!el) {
    return { mode: "fa", faFamily: "fa-solid", faIcon: "fa-calendar", size: 16, color: "#0f172a", svg: "", show: true };
  }
  const mode = (el.getAttribute("data-calendar-icon-mode") || "fa").toLowerCase() === "svg" ? "svg" : "fa";
  const faFamily = el.getAttribute("data-calendar-icon-family") || "fa-solid";
  const faIcon = el.getAttribute("data-calendar-icon-name") || "fa-calendar";
  const size = parseInt(el.getAttribute("data-calendar-icon-size") || "16", 10) || 16;
  const color = el.getAttribute("data-calendar-icon-color") || "#0f172a";
  const svg = el.getAttribute("data-calendar-icon-svg") || "";
  const show = el.getAttribute("data-calendar-icon-show") !== "false";
  return { mode, faFamily, faIcon, size, color, svg, show };
}

function CalendarIcon() {
  const [spec, setSpec] = useState(readCalendarIconSpec);

  useEffect(() => {
    const el = getHostEl();
    if (!el) return;
    const sync = () => setSpec(readCalendarIconSpec());
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        const n = m.attributeName || "";
        if (
          n === "data-calendar-icon-mode" ||
          n === "data-calendar-icon-family" ||
          n === "data-calendar-icon-name" ||
          n === "data-calendar-icon-size" ||
          n === "data-calendar-icon-color" ||
          n === "data-calendar-icon-svg" ||
          n === "data-calendar-icon-show"
        ) {
          sync();
        }
      }
    });
    mo.observe(el, { attributes: true });
    return () => mo.disconnect();
  }, []);

  if (!spec.show) return null;

  if (spec.mode === "svg" && spec.svg) {
    return (
      <span
        aria-hidden="true"
        style={{ display: "inline-grid", width: spec.size, height: spec.size, color: spec.color, placeItems: "center" }}
        dangerouslySetInnerHTML={{ __html: spec.svg }}
      />
    );
  }

  return (
    <i
      aria-hidden="true"
      className={`${spec.faFamily} ${spec.faIcon}`}
      style={{ fontSize: spec.size, color: spec.color, lineHeight: 1 }}
    />
  );
}

/**
 * CalendarPane - Date picker with dropdown calendar (2 years worth)
 * Compatible with CalendarWidgetStyleLayer.jsx styling
 *
 * @param {boolean} alwaysOpen - Keep calendar open (no dropdown behavior)
 * @param {boolean} fullWidth - Fill container width
 * @param {boolean} hideInput - Hide the input field (useful for inline body embeds)
 */
export default function CalendarPane({ palette, alwaysOpen = false, fullWidth = true, hideInput = false }) {
  const [isOpen, setIsOpen] = useState(alwaysOpen);
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);
  const [flexibilityDays, setFlexibilityDays] = useState(0);
  const [placeholder, setPlaceholder] = useState(() => {
    const el = getHostEl();
    return el?.getAttribute("data-calendar-placeholder") || "Dates";
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Update placeholder when data attribute changes
  useEffect(() => {
    const el = getHostEl();
    if (!el) return;
    const sync = () => {
      const newPlaceholder = el.getAttribute("data-calendar-placeholder") || "Dates";
      setPlaceholder(newPlaceholder);
    };
    const mo = new MutationObserver(() => sync());
    mo.observe(el, { attributes: true, attributeFilter: ["data-calendar-placeholder"] });
    return () => mo.disconnect();
  }, []);

  // Get current date
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Sync isOpen with alwaysOpen prop
  useEffect(() => {
    if (alwaysOpen) {
      setIsOpen(true);
    }
  }, [alwaysOpen]);

  // Read URL parameters (startDate, endDate, flexibilityDays) on mount and highlight those dates
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const startDateParam = params.get("startDate");
    const endDateParam = params.get("endDate");
    const flexibilityParam = params.get("flexibilityDays");

    if (startDateParam) {
      const start = new Date(startDateParam + "T00:00:00");
      if (!isNaN(start.getTime())) {
        setSelectedStart(start);
      }
    }

    if (endDateParam) {
      const end = new Date(endDateParam + "T00:00:00");
      if (!isNaN(end.getTime())) {
        setSelectedEnd(end);
      }
    }

    if (flexibilityParam) {
      const flexDays = parseInt(flexibilityParam, 10);
      if (!isNaN(flexDays) && [0, 1, 2, 3, 7, 14].includes(flexDays)) {
        setFlexibilityDays(flexDays);
      }
    }
  }, []);

  // Update URL when flexibilityDays changes
  useEffect(() => {
    const url = new URL(window.location);
    if (flexibilityDays > 0) {
      url.searchParams.set('flexibilityDays', String(flexibilityDays));
    } else {
      url.searchParams.delete('flexibilityDays');
    }
    window.history.replaceState({}, '', url);
  }, [flexibilityDays]);

  // Close dropdown when clicking outside (unless alwaysOpen)
  useEffect(() => {
    if (alwaysOpen) return; // Don't close if alwaysOpen

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [alwaysOpen]);

  // Format selected dates for input display
  const getDisplayValue = () => {
    if (!selectedStart) return "";
    if (!selectedEnd) {
      return selectedStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    const startStr = selectedStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endStr = selectedEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${startStr} - ${endStr}`;
  };

  // Generate calendar for a specific month
  const generateCalendar = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateSelected = (date) => {
    if (!date || !selectedStart) return false;
    if (!selectedEnd) return date.getTime() === selectedStart.getTime();
    return date >= selectedStart && date <= selectedEnd;
  };

  const isDateInRange = (date) => {
    if (!date || !selectedStart || !selectedEnd) return false;
    return date > selectedStart && date < selectedEnd;
  };

  // Format date to YYYY-MM-DD in local timezone (avoids UTC conversion issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (date) => {
    if (!date) return;

    let newStart = selectedStart;
    let newEnd = selectedEnd;

    if (!selectedStart || (selectedStart && selectedEnd)) {
      newStart = date;
      newEnd = null;
      setSelectedStart(date);
      setSelectedEnd(null);
    } else {
      if (date < selectedStart) {
        newEnd = selectedStart;
        newStart = date;
        setSelectedEnd(selectedStart);
        setSelectedStart(date);
      } else {
        newEnd = date;
        setSelectedEnd(date);
      }
    }

    // Update URL with date parameters when both dates are selected
    if (newStart && newEnd) {
      const startDateStr = formatDateLocal(newStart);
      const endDateStr = formatDateLocal(newEnd);
      const url = new URL(window.location);
      url.searchParams.set('startDate', startDateStr);
      url.searchParams.set('endDate', endDateStr);
      window.history.pushState({}, '', url);

      // Close the calendar when both dates are selected (unless alwaysOpen)
      if (!alwaysOpen) {
        setIsOpen(false);
      }
    }
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };

  const renderMonth = (monthOffset) => {
    const month = (currentMonth + monthOffset) % 12;
    const year = currentYear + Math.floor((currentMonth + monthOffset) / 12);
    const days = generateCalendar(month, year);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    return (
      <div key={`${month}-${year}`} className="calendar-month" style={{ minWidth: "280px", marginBottom: "32px" }}>
        {/* Month Header */}
        <div className="calendar-header" style={{ textAlign: "center", marginBottom: "16px" }}>
          <h4 style={{ margin: 0 }}>{monthNames[month]} {year}</h4>
        </div>

        {/* Weekday Headers */}
        <ul className="weekdays" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", listStyle: "none", padding: 0, margin: "0 0 8px 0" }}>
          {weekDays.map((day) => (
            <li key={day} style={{ textAlign: "center", padding: "8px 4px" }}>
              {day}
            </li>
          ))}
        </ul>

        {/* Calendar Days */}
        <div className="calendar-days" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} style={{ aspectRatio: "1" }} />;
            }

            const isPast = isPastDate(date);
            const isSelected = isDateSelected(date);
            const isInRange = isDateInRange(date);
            const isStart = selectedStart && date.getTime() === selectedStart.getTime();
            const isEnd = selectedEnd && date.getTime() === selectedEnd.getTime();

            // Build class names for styling layer
            let dayClasses = "nexus-calendar-day";
            if (isPast) {
              dayClasses += " past-day day-disabled";
            }
            if (isStart) {
              dayClasses += " checkin-date";
            }
            if (isEnd) {
              dayClasses += " checkout-date";
            }
            if (isInRange) {
              dayClasses += " in-range";
            }

            return (
              <button
                key={index}
                onClick={() => !isPast && handleDateClick(date)}
                disabled={isPast}
                className={dayClasses}
                style={{
                  aspectRatio: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid",
                  borderRadius: "8px",
                  cursor: isPast ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Styles
  const rootStyle = {
    position: "relative",
    width: fullWidth ? "100%" : "auto",
  };

  const pill = {
    display: "flex",
    alignItems: "center",
    gap: "var(--sw-pill-gap, 10px)",
    border: "1px solid var(--sw-cal-bc, rgba(15,23,42,.2))",
    borderRadius: "var(--sw-cal-r, 10px)",
    padding: "var(--sw-cal-py, 10px) var(--sw-cal-px, 12px)",
    background: "var(--sw-cal-bg, transparent)",
    cursor: "pointer",
    userSelect: "none",
    justifyContent: "space-between",
    width: fullWidth ? "100%" : "auto",
    minWidth: "var(--sw-cal-minw, 150px)",
    height: "var(--sw-cal-h, 44px)",
    fontFamily: "var(--sw-cal-ff, inherit)",
    fontSize: "var(--sw-cal-fs, 14px)",
    fontWeight: "var(--sw-cal-fw, 500)",
    color: "var(--sw-cal-fc, #0f172a)",
    boxSizing: "border-box",
  };

  return (
    <div style={rootStyle}>
      {/* Input Field Wrapper - Hidden when hideInput is true */}
      {!hideInput && (
        <div
          className="nexus-calendar-input-container"
          onClick={() => !alwaysOpen && setIsOpen(!isOpen)}
          style={pill}
        >
          <span aria-hidden="true">
            <CalendarIcon />
          </span>
          <input
            ref={inputRef}
            type="text"
            readOnly
            placeholder={placeholder}
            value={getDisplayValue()}
            className="nexus-calendar-datepicker"
            style={{
              flex: 1,
              cursor: "pointer",
              border: "none",
              background: "transparent",
              padding: 0,
              outline: "none",
              fontSize: "inherit",
              fontWeight: "inherit",
              fontFamily: "inherit",
              color: "inherit",
              WebkitTextFillColor: "inherit",
            }}
          />
          <style>{`
            .nexus-calendar-datepicker::placeholder {
              color: var(--sw-cal-placeholder, #9ca3af);
            }
          `}</style>
        </div>
      )}

      {/* Modal Calendar */}
      {isOpen && (
        <>
          {/* Modal Backdrop - Only show when NOT alwaysOpen */}
          {!alwaysOpen && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                zIndex: 9998,
              }}
              onClick={() => setIsOpen(false)}
            />
          )}
          {/* Modal Content */}
          <div
            ref={dropdownRef}
            data-nexus-calendar
            className="nexus-calendar-section-dropdown"
            style={{
              position: alwaysOpen ? "relative" : "fixed",
              top: alwaysOpen ? "auto" : "50%",
              left: alwaysOpen ? "auto" : "50%",
              transform: alwaysOpen ? "none" : "translate(-50%, -50%)",
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              zIndex: alwaysOpen ? 1 : 9999,
              width: "auto",
              height: "500px",
              minHeight: "500px",
              maxWidth: alwaysOpen ? "100%" : "90vw",
              maxHeight: alwaysOpen ? "none" : "90vh",
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              marginTop: alwaysOpen ? "16px" : "0",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              padding: "24px",
            }}
          >
            {/* 24 months grid (2 years) - 2 columns on desktop, 1 on mobile/tablet */}
            <div
              className="calendar-months-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 280px)",
                gap: "32px",
                flex: "1",
                overflowY: "auto",
                paddingRight: "8px",
              }}
            >
              {Array.from({ length: 24 }, (_, i) => renderMonth(i))}
            </div>

            {/* Date flexibility options */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center", paddingTop: "16px", marginTop: "16px", borderTop: "1px solid #e5e7eb", flexShrink: 0 }}>
              <button
                data-flex-btn
                onClick={() => setFlexibilityDays(0)}
                className={flexibilityDays === 0 ? "active" : ""}
                style={{
                  padding: "8px 16px",
                  borderRadius: "24px",
                  border: "1px solid",
                  borderColor: flexibilityDays === 0 ? "#111827" : "#d1d5db",
                  background: flexibilityDays === 0 ? "#111827" : "white",
                  color: flexibilityDays === 0 ? "white" : "#374151",
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                Exact dates
              </button>
              {[1, 2, 3, 7, 14].map((days) => (
                <button
                  key={days}
                  data-flex-btn
                  onClick={() => setFlexibilityDays(days)}
                  className={flexibilityDays === days ? "active" : ""}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "24px",
                    border: "1px solid",
                    borderColor: flexibilityDays === days ? "#111827" : "#d1d5db",
                    background: flexibilityDays === days ? "#111827" : "white",
                    color: flexibilityDays === days ? "white" : "#374151",
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  ± {days} day{days > 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .calendar-months-grid {
            grid-template-columns: 280px !important;
          }
        }
      `}</style>
    </div>
  );
}
